import { useApplicantAPI } from "@certn/api";
import { Text, PrimaryButton, showToast } from "@certn/cascadia";
import {
  Container,
  Divider,
  Loader,
  SectionMessage,
} from "@certn/cascadia-react";
import { addDays, format } from "date-fns";
import { t } from "i18next";
import { useContext, useEffect, useState } from "react";
import { Trans } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { DCOReviewContext } from "../DCOReviewContext";
import { DCOReviewPageLayout } from "../DCOReviewLayout";

export function ReviewLandingStep() {
  const { data: cases, isLoading } = useApplicantAPI(
    "/api/internal/applicant/case/complete/"
  );
  const { caseId } = useParams();

  const { data: dcoTimeResponse, isLoading: dcoisLoading } = useApplicantAPI(
    `/api/internal/applicant/case/${caseId}/dco-notification-timestamp/`
  );

  const { goToNextStep } = useContext(DCOReviewContext);
  const navigate = useNavigate();

  const [deadlineDate, setDeadlineDate] = useState<string>(
    "your notification date + 2 days"
  );

  const can_user_view_case = cases?.some((c) => c.short_id === caseId);
  const notificationTime = dcoTimeResponse?.dispute_notification_timestamp;

  useEffect(() => {
    if (
      (!isLoading && !can_user_view_case) ||
      (!notificationTime && !dcoisLoading)
    ) {
      showToast("Invalid link", { type: "error" });
      navigate("/");
    }

    if (notificationTime) {
      const parsedDate = new Date(notificationTime);
      const datePlus2Days = addDays(parsedDate, 2);
      const formatted = format(
        datePlus2Days,
        "EEEE, MMMM dd yyyy HH:mm:ss 'AEST'"
      );
      setDeadlineDate(formatted);
    }
  }, [isLoading, navigate, can_user_view_case, notificationTime, dcoisLoading]);

  return (
    <DCOReviewPageLayout onComplete={goToNextStep} nextDisabled={false}>
      {isLoading || dcoisLoading ? (
        <Loader />
      ) : (
        <Container>
          <Text
            style="global/body-primary"
            color="text-weak"
            testID="review-landing-subtext-1"
          >
            <Trans>
              The result of your Nationally Coordinated Criminal History Check
              has been finalised and contains a Disclosable Court Outcome. As
              part of our obligations as an Accredited Body with the Australian
              Criminal Intelligence Commission (ACIC), we are required to give
              you the opportunity to dispute some or all of the information
              returned.
            </Trans>
          </Text>

          <Text
            style="global/body-primary"
            color="text-weak"
            testID="review-landing-subtext-2"
          >
            <Trans>
              Follow the next steps to review your Check Results Report.
            </Trans>
          </Text>
          <SectionMessage
            type="info"
            icon="info"
            testID="review-landing-subtext-3"
            sectionTitle={t("Important")}
          >
            <Text
              style="global/body-primary"
              color="text-weak"
              testID="review-landing-greytext-2"
            >
              <Trans>
                You must dispute your results by <b>{deadlineDate}</b>. If no
                action is taken, your results will be automatically shared with
                your employer or designated third party.
              </Trans>
            </Text>
          </SectionMessage>
          <Divider />
          <PrimaryButton fullWidth onPress={goToNextStep}>
            Continue
          </PrimaryButton>
        </Container>
      )}
    </DCOReviewPageLayout>
  );
}
